'use strict';

import BamTeamData from 'bam-team-data';
const cloneDeep = BamTeamData.getHelpers().cloneDeep;

/**
 * "Private" static functions used by BamLinker and subclasses.  Other consumers should not call these
 * methods.
 * @private
 */
class Helpers {

    /**
     * Validates options passed to the BamLinker constructor, throws an error if invalid.
     * @param options The options object passed to the constructor
     * @param supportedOptionSpecs SUPPORTED_OPTION_SPECS
     * @throws Error if options are not valid
     */
    static validateOptions(candidateOptions, supportedOptionSpecs) {
        let errMsg = null;
        let options = (!candidateOptions ? {} : candidateOptions);

        // Options must be an object:
        if (typeof options !== 'object' || Array.isArray(options)) {
            errMsg = 'is not an object.';
        }

        // Each user-specified option must conform to its spec:
        if (!errMsg) {
            let optionKeys = Object.keys(options);
            let optionSpec;
            let optionValue;
            let optionValueType;
            let optionKey;
            for (let i = 0, ln = optionKeys.length; i < ln; i++) {
                optionKey = optionKeys[i];
                optionSpec = supportedOptionSpecs[optionKey];
                if (optionSpec === undefined) {
                    errMsg = 'contains unsupported option: ' + optionKey;
                    break;
                }
                optionValue = options[optionKey];
                optionValueType = (Array.isArray(optionValue) ? 'array' : typeof optionValue);
                if (optionValue !== null) {
                    if (optionValueType !== optionSpec.expectedType) {
                        errMsg = 'contains option with unexpected value type for "' + optionKey +
                            '" (expected: ' + optionSpec.expectedType + '; found: ' + optionValueType + ')';
                        break;
                    }
                }
            }
        }

        // Required options must be present:
        let specKeys = Object.keys(supportedOptionSpecs);
        let specKey;
        for (let i = 0, ln = specKeys.length; i < ln; i++) {
            specKey = specKeys[i];
            if (!supportedOptionSpecs[specKey].required) {
                continue;
            }
            if (options[specKey] === undefined) {
                errMsg = 'did not contain a required option: ' + specKey;
                break;
            }
        }

        if (errMsg) {
            throw new Error('The options arg you passed ' + errMsg);
        }
    }

    /**
     * Return an object whose properties are option keys and values are default values for those options
     * @param supportedOptionSpecs
     * @return {{}}
     */
    static getDefaultOptions(supportedOptionSpecs) {
        let defaultOptions = {};
        let optionKeys = Object.keys(supportedOptionSpecs);
        let key;
        for (let i = 0, ln = optionKeys.length; i < ln; i++) {
            key = optionKeys[i];
            defaultOptions[key] = supportedOptionSpecs[key].defaultValue;
        }
        return defaultOptions;
    }

    /**
     * Returns the value of a call to the cloneDeep method, which returns a deep copy of the passed value.
     * @param arg
     */
    static cloneDeep(arg) {
        return cloneDeep(arg);
    }

    /**
     * A generic method which subclass implementations may use to derive and add request-specific values to
     * subclass-constructor
     * options (langCode, teamId, deviceType, and appView).  Ideally this method will work for all BANE sites, but
     * it seems likely that there will be exceptions.
     * @param generatorConstructorOptions Options which will be passed to the BamLinker constructor from
     *   a BamLinker subclass's constructor; langCode, teamId, deviceType, and appView properties are added
     *   to this object
     * @param windowOrReq A BANE-controller `req` or a browser `window`
     * @param addDefaultsOnError If true and this method throws an error, the error will be swallowed and default values
     *   will be added to the options object.  This should only be true for BamLinker subclass constructors
     *   which are in development, and thus can't depend on being able to find values where they should be.
     */
    static addRequestSpecificOptions(generatorConstructorOptions, windowOrReq, addDefaultsOnError) {
        try {
            generatorConstructorOptions.windowOrReq = windowOrReq;
            if (windowOrReq.navigator) {
                // windowOrReq is a browser window:
                generatorConstructorOptions.clientSide = true;
                let metaTags = windowOrReq.document.getElementsByTagName('meta');
                generatorConstructorOptions.appView = (Helpers._getMetaTagContent(metaTags, 'app-view') || 'web');
                generatorConstructorOptions.langCode = (Helpers._getMetaTagContent(metaTags, 'locale') || 'en');
                generatorConstructorOptions.deviceType = 'desktop'; // hard-coding for now
                generatorConstructorOptions.teamId = parseInt(Helpers._getMetaTagContent(metaTags, 'team_id') || 0);
            } else if (windowOrReq.configFinder && windowOrReq.i18n) {
                // windowOrReq is a BANE-controller request:
                generatorConstructorOptions.clientSide = false;
                generatorConstructorOptions.appView = windowOrReq.configFinder.appView;
                generatorConstructorOptions.langCode = windowOrReq.configFinder.getLocale();
                generatorConstructorOptions.deviceType = 'desktop'; // hard-coding for now
                generatorConstructorOptions.teamId = parseInt(
                    (windowOrReq.configFinder.clubConfig && windowOrReq.configFinder.clubConfig.team)
                        ? (windowOrReq.configFinder.clubConfig.team.team_id || 0)
                        : 0);
            } else {
                throw new Error('The "windowOrReq" constructor param must be either a BANE browser window or ' +
                    'a BANE-controller "req" obj.');
            }
        } catch (e) {
            if (!addDefaultsOnError) {
                throw e;
            }
            console.warn('Error thrown getting constructor-option values from "' + windowOrReq + '"; swallowing ' +
                'this error and adding default option values because addDefaultsOnError is true (it should only be ' +
                'true in development).  Error:');
            console.warn(e);
            if (console.trace) {
                console.trace();
            }
            generatorConstructorOptions.appView = 'web';
            generatorConstructorOptions.langCode = 'en';
            generatorConstructorOptions.deviceType = 'desktop';
            generatorConstructorOptions.teamId = 0;
        }
    }

    /**
     * Returns the content (i.e., the value) of the meta tag identified by `key`
     * @param metaTagEls Typically the value returned by window.document.getElementsByTagName('meta')
     * @param key The meta tag's name, property, or http-equiv attribute value
     * @return {string} The meta tag's content (its value), or null if the tag wasn't found
     * @private
     */
    static _getMetaTagContent(metaTagEls, key) {
        let metaTagKeyAttrNames = ['name', 'property', 'http-equiv'];

        for (var k = 0, kln = metaTagKeyAttrNames.length; k < kln; k++) {
            for (var i = 0, mln = metaTagEls.length; i < mln; i++) {
                if (metaTagEls[i].getAttribute(metaTagKeyAttrNames[k]) === key) {
                    return metaTagEls[i].getAttribute('content');
                }
            }
        }
        // sorry, no luck:
        return null;
    }

    /**
     * Methods should call this if the base class doesn't implement the method, relying on subclasses to do so.
     * @param methodName The name of the unimplemented method, for the Error message
     * @throws Error when called.
     */
    static throwNotImplementedError(methodName) {
        throw new Error(methodName + '() is not implemented in the BamLinker, and the subclass has not provided' +
            'an implementation either.');
    }


    /**
     * Passed a date in format 2016-08-17T00:05:00Z, return 2016-08-16 (the date in EST)
     *
     * Implementation note: not using moment here because don't want the overhead in this base class which will often
     * be a part of basesite.  Thus, doing some Date hackery instead (note: hackery has to be browser-friendly too, not
     * only ES6).
     *
     * @param dateStr A date in format '2016-08-17T00:05:00Z' (e.g. as returned by statsapi)
     * @param gmtOffsetHours The offset, in hours; e.g. EST offset is -5
     * @param delimiter char to separate year, month, date
     * @return {string} e.g. 2016-08-16
     */
    static getYMDStrFromStatsapiDate(dateStr, gmtOffsetHours, delimiter) {
        let date = new Date(
            parseInt(dateStr.substring(0, 4)), // year
            parseInt(dateStr.substring(5, 7)) - 1, // month
            parseInt(dateStr.substring(8, 10)), // day in month
            parseInt(dateStr.substring(11, 13)), // hours
            parseInt(dateStr.substring(14, 16)), // minutes
            parseInt(dateStr.substring(17, 19)), // seconds
            0 // millis
        );

        let offsetMillis = gmtOffsetHours * 1000 * 60 * 60; // likely negative, e.g. if offset is ET
        date.setTime(date.getTime() + offsetMillis);
        let month = '' + (date.getMonth() + 1);
        let dateInMonth = '' + date.getDate();
        month = (month.length === 1 ? '0' + month : month);
        dateInMonth = (dateInMonth.length === 1 ? '0' + dateInMonth : dateInMonth);
        return '' + date.getFullYear() + delimiter + month + delimiter + dateInMonth;
    }
}

export default Helpers;
